/*  MUI  */
import { Box, Paper } from '@mui/material';

/*  Shared  */
import { SaasOpsKebabMenu } from 'application/Home/Admin/Workato/Components/SaasOpsKebabMenu';
import {
    AppNameLinkCell,
    DateFormatCell,
    UsageChartCell,
    dateFilter,
    lastXDays
} from 'application/Shared/Components/Cells';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';
import { sortByWorkatoProvider } from 'application/Shared/Components/Table/tableSortingFunctions';
import { ColumnDef } from 'application/Shared/Components/Table/types';
import { sortByTimestamp } from 'application/Shared/Components/TableSortFunctions';
import { TABLENAMES } from 'application/Shared/Constants';
import { Route } from 'application/Shared/Helpers';
import { UserApp } from 'application/Shared/types/userApp';

/* Yarn */
import { TFunction } from 'i18next';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const userAppsTableColumns = ({
    t,
    userId
}: {
    route: string;
    t: TFunction<'translation', undefined>;
    userId: string | undefined;
}): ColumnDef<UserApp, string>[] => [
    {
        accessorKey: 'application.name',
        cell: (props) => (
            <AppNameLinkCell fromPage={t('Common.user')} {...props} />
        ),
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'application',
        name: 'Common.application',
        size: 300,
        testId: 'application'
    },
    {
        accessorKey: 'usageChart',
        cell: UsageChartCell,
        enableColumnFilter: false,
        enableHiding: false,
        enableSorting: false,
        header: Header,
        id: 'usageChart',
        name: 'Common.usage',
        size: 100,
        testId: 'usageChart'
    },
    {
        accessorKey: 'dateCreated',
        cell: (props) => <DateFormatCell {...props} noTz={false} />,
        filterFn: lastXDays,
        header: Header,
        id: 'dateCreated',
        meta: { filter: dateFilter },
        name: 'Common.dateAdded',
        size: 200,
        sortingFn: sortByTimestamp,
        testId: 'dateCreated'
    },
    {
        accessorKey: 'actions',
        cell: ({ row }) => {
            const { application } = row.original;
            return (
                <SaasOpsKebabMenu
                    application={application!}
                    dataTrackId="usersTab"
                    userId={userId!}
                />
            );
        },
        enableColumnFilter: false,
        enableHiding: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        maxSize: 10,
        meta: {
            align: 'right',
            header: {
                justifyContent: 'flex-end'
            }
        },
        name: 'Common.actions',
        size: 1,
        testId: 'actions'
    }
];

const toggles = (t: TFunction<'translation', undefined>) => [
    {
        defaultEnabled: true,
        key: 'excludePersonal',
        parameter: { field: 'excludePersonal', value: true },
        toggleText: t('Common.exclude_personal_applications')
    }
];

/**
 * Handles sorting of user application rows
 * @param {UserAppType[]} rows - Array of user application objects to sort
 * @returns {UserAppType[]} Sorted array of user applications with Workato providers first, then sorted alphabetically by name
 * @description Sorts rows by:
 * 1. Applications with Workato providers first
 * 2. If both have Workato providers, sort alphabetically by name
 * 3. If neither have providers, sort alphabetically by name
 */

export const UserAppsTable = () => {
    const { t } = useTranslation();
    const { clientId, partnerId, userId } = useParams();

    const route = Route.buildBaseAdminRoute({ clientId: clientId!, partnerId });

    const parameters = useMemo(
        () => ({
            includeApplication: true,
            includeUnclassified: true,
            includeWorkato: true,
            platformUserId: userId
        }),
        [userId]
    );

    const columnVisibility = {
        actions: true,
        application: true,
        dateAdded: true,
        usage: true
    };

    const tableToggles = useMemo(() => toggles(t), [t]);

    return (
        <Box data-testid="userAppTable">
            <ManagedTableV2ContextProvider
                baseParameters={parameters}
                columns={userAppsTableColumns({
                    route,
                    t,
                    userId
                })}
                handleRows={sortByWorkatoProvider}
                ignoreSearchParams
                initialState={{
                    columnVisibility
                }}
                mode="client"
                rowsApi="/userApp"
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                sortDirection="ASC"
                tableName={TABLENAMES.USER_APPS}
                toggles={tableToggles}
            >
                <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{ mt: '24px', p: 0 }}
                >
                    <ManagedTableV2UI
                        csvExportFileName="license_user_breakdown"
                        renderFooter
                        titleDef={{ titleKey: 'Common.applications' }}
                    />
                </Paper>
            </ManagedTableV2ContextProvider>
        </Box>
    );
};
